import React, { useState, useRef, useEffect, useId, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
}

/**
 * Arc-style MultiSelect component
 * 
 * Features:
 * - Multiple selection with chips
 * - Custom dropdown with smooth animations
 * - Keyboard navigation
 * - Click outside to close
 */
const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options = [],
      value,
      defaultValue = [],
      onChange,
      placeholder = "Select options",
      label,
      error,
      helperText,
      size = "md",
      fullWidth = true,
      disabled = false,
      required = false,
      className = "",
      id,
    },
    ref
  ) => {
    const generatedId = useId();
    const selectId = id || generatedId;
    const hasError = !!error;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState<string[]>(
      value || defaultValue
    );
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // Sync with controlled value
    useEffect(() => {
      if (value !== undefined) {
        setSelectedValues(value);
      }
    }, [value]);

    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Keyboard navigation
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev < options.length - 1 ? prev + 1 : 0
            );
            break;
          case "ArrowUp":
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : options.length - 1
            );
            break;
          case "Enter":
            e.preventDefault();
            if (highlightedIndex >= 0 && !options[highlightedIndex].disabled) {
              toggleOption(options[highlightedIndex].value);
            }
            break;
          case "Escape":
            e.preventDefault();
            setIsOpen(false);
            break;
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, highlightedIndex, options]);

    const toggleOption = (optionValue: string) => {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];

      setSelectedValues(newValues);
      onChange?.(newValues);
    };

    const removeOption = (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const newValues = selectedValues.filter((v) => v !== optionValue);
      setSelectedValues(newValues);
      onChange?.(newValues);
    };

    const selectedOptions = options.filter((opt) =>
      selectedValues.includes(opt.value)
    );

    // Size styles
    const sizeStyles = {
      sm: {
        trigger: "py-1.5 px-3 text-sm min-h-[36px]",
        label: "text-xs mb-1.5",
        chip: "px-2 py-0.5 text-xs",
        option: "py-1.5 px-3 text-sm",
      },
      md: {
        trigger: "py-2 px-4 text-sm min-h-[44px]",
        label: "text-sm mb-2",
        chip: "px-2.5 py-1 text-xs",
        option: "py-2 px-4 text-sm",
      },
      lg: {
        trigger: "py-3 px-5 text-base min-h-[52px]",
        label: "text-sm mb-2",
        chip: "px-3 py-1.5 text-sm",
        option: "py-3 px-5 text-base",
      },
    };

    const currentSize = sizeStyles[size];

    return (
      <div
        ref={ref}
        className={`relative ${fullWidth ? "w-full" : ""} ${className}`}
      >
        {label && (
          <label
            htmlFor={selectId}
            className={`block font-semibold text-content-primary ${currentSize.label}`}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div ref={containerRef} className="relative">
          {/* Trigger button */}
          <button
            id={selectId}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={`
              w-full
              flex flex-wrap items-center gap-1.5
              bg-surface-elevated
              border rounded-arc-md
              font-medium
              text-left
              transition-all duration-arc-normal ease-arc
              outline-none
              ${currentSize.trigger}
              ${
                hasError
                  ? "border-transparent ring-2 ring-error-main"
                  : isOpen
                  ? "border-transparent ring-2 ring-accent"
                  : "border-arc-border hover:border-arc-border-strong focus:border-transparent focus:ring-2 focus:ring-accent"
              }
              ${
                disabled
                  ? "bg-surface-tertiary text-content-tertiary cursor-not-allowed"
                  : "cursor-pointer"
              }
            `}
          >
            {selectedOptions.length > 0 ? (
              <>
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className={`
                      inline-flex items-center gap-1
                      bg-accent-muted text-accent-dark
                      rounded-arc-sm font-medium
                      ${currentSize.chip}
                    `}
                  >
                    {option.label}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => removeOption(option.value, e)}
                        className="hover:text-accent transition-colors ml-0.5"
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </>
            ) : (
              <span className="text-content-tertiary">{placeholder}</span>
            )}
            <svg
              className={`w-4 h-4 text-content-tertiary transition-transform duration-arc-fast ml-auto ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="absolute z-50 w-full mt-1 bg-surface-elevated border border-arc-border rounded-arc-md shadow-arc-lg overflow-hidden"
              >
                <ul
                  ref={listRef}
                  className="max-h-60 overflow-y-auto py-1"
                  role="listbox"
                >
                  {options.map((option, index) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <li
                        key={option.value}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() =>
                          !option.disabled && toggleOption(option.value)
                        }
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={`
                          ${currentSize.option}
                          flex items-center gap-3
                          cursor-pointer
                          transition-colors duration-arc-fast
                          ${
                            option.disabled
                              ? "text-content-tertiary cursor-not-allowed"
                              : isSelected
                              ? "bg-accent-muted text-accent-dark"
                              : highlightedIndex === index
                              ? "bg-surface-secondary"
                              : "hover:bg-surface-secondary"
                          }
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="accent-accent cursor-pointer"
                          disabled={option.disabled}
                        />
                        {option.label}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {(error || helperText) && (
          <p
            className={`mt-1.5 text-xs ${
              hasError ? "text-error" : "text-content-tertiary"
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export default MultiSelect;



